import { QUERY_KEY_CONVERSATION_TASK } from '@terminal/react-query-hooks/alfred/useConversationTask';
import {
  QUERY_KEY_EVENT_ALERTS,
  UNREAD_COUNT_QUERY_KEY,
} from '@terminal/react-query-hooks/events/use-event-alerts/event-alerts-query-keys';
import {
  faviconLinks,
  updateAlertsFavicon,
} from '@terminal/react-query-hooks/events/use-event-alerts/updateAlertsFavicon';
import { postAlerts } from '@terminal/services/event-alerts/event-alerts-service';
import { queryClient } from '@terminal/services/query-client/queryClient';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { useNotifications } from '@terminal/stores/use-notifications/useNotifications';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { usePortfolio } from '@terminal/views/settings/portfolio-view/use-portfolio/usePortfolio';
import { useEffect } from 'react';

export const useNotificationsListener = () => {
  const token = useAuthStore(s => s.token);
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const isAlertsEnabled = !isBasicOrLightUser;
  const handlePortfolioWebsocketResponse = usePortfolio(
    state => state.handlePortfolioWebsocketResponse
  );

  const { subscribeToMessageOfType, unsubscribe } = useNotifications(state => ({
    subscribeToMessageOfType: state.subscribeToMessageOfType,
    unsubscribe: state.unsubscribe,
  }));
  const {
    getConversationStates,
    markConversationReadStatus,
    markTaskReceivedWsUpdate,
  } = useChat(state => ({
    getConversationStates: state.getConversationStates,
    markConversationReadStatus: state.markConversationReadStatus,
    markTaskReceivedWsUpdate: state.markTaskReceivedWsUpdate,
  }));

  const handleConversationUpdate = async ({
    conversationId,
    taskId,
    isFailed,
    isCancelled = false,
  }: {
    conversationId: string;
    taskId: string;
    isFailed: boolean;
    isCancelled?: boolean;
  }) => {
    markTaskReceivedWsUpdate(conversationId, taskId);
    await getConversationStates({ conversationId, taskId });

    if (!isFailed) {
      markConversationReadStatus(conversationId, isCancelled);
    }
  };

  const toggleFavicon = (key: keyof typeof faviconLinks) => {
    const faviconElement = document.querySelector("link[rel*='icon']");
    faviconElement?.setAttribute('href', faviconLinks[key]);
  };

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    if (isAlertsEnabled) {
      queryClient
        .fetchInfiniteQuery({
          queryKey: UNREAD_COUNT_QUERY_KEY,
          queryFn: () => postAlerts({ count_only: true, read: false }),
          initialPageParam: null as string | null,
        })
        .then(updateAlertsFavicon);
    } else {
      return useChat.subscribe(state => {
        const completeUnreadConversationsCount =
          state.getCompleteUnreadConversations().length;

        toggleFavicon(
          completeUnreadConversationsCount ? 'notification' : 'default'
        );
      });
    }

    return undefined;
  }, [isAlertsEnabled]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    return subscribeToMessageOfType('portfolio', async response => {
      handlePortfolioWebsocketResponse(response);
    });
  }, [token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const unsubscribeConversation = subscribeToMessageOfType(
      'conversation',
      async response => {
        const { conversation_id: conversationId, request_id: requestId } =
          response.metadata;

        await handleConversationUpdate({
          conversationId,
          taskId: requestId,
          isFailed: response.event.action === 'request.fail',
          isCancelled: response.event.action === 'request.cancel',
        });
      }
    );

    if (isAlertsEnabled) {
      const unsubscribeAlert = subscribeToMessageOfType('alert', async () => {
        await queryClient.refetchQueries({
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          exact: false,
          type: 'all',
        });
        queryClient.refetchQueries({
          queryKey: [QUERY_KEY_CONVERSATION_TASK],
          exact: false,
          type: 'all',
        });
        updateAlertsFavicon();
      });

      return () => {
        unsubscribeConversation();
        unsubscribeAlert();
        unsubscribe();
      };
    }

    return () => {
      unsubscribeConversation();
      unsubscribe();
    };
  }, [token, isAlertsEnabled]);
};
